/**
 * Reset an OAuth button's loading state when the user comes BACK to the auth
 * screen without completing the flow (browser Back from Google, an aborted
 * redirect, or a cancelled attempt restored from the back/forward cache).
 *
 * Without this, the component keeps its pre-redirect state — the button
 * stays on "Redirecting…" and stays disabled until the app is fully
 * reopened.
 *
 * Returns an unsubscribe function (React effect cleanup).
 */
export function onOAuthReturn(
  reset: () => void,
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>
): () => void {
  const win = target ?? (typeof window !== 'undefined' ? window : undefined);
  if (!win) return () => undefined;

  const handler = (event: Event) => {
    // persisted=true means the page was restored from the back/forward cache
    // with its old React state (isGoogleLoading=true). A fresh load always
    // starts with the idle state, so only bfcache restores need the reset.
    if ((event as PageTransitionEvent).persisted) reset();
  };

  win.addEventListener('pageshow', handler);
  return () => win.removeEventListener('pageshow', handler);
}
